import { mainApi } from '../../../../api/mainApi';
import { middlewareApi } from '../../../middleware/middlewareApi';
import { Pensum, PensumResponse } from '../interfaces/pensums.interface';
import { setPensumList } from './pensumsSlice';

export const pensumTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['Pensums', 'PensumsAvailables'],
});
// Define a service using a base URL and expected endpoints
export const pensumApi = pensumTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all pensums
     */
    getPensums: builder.query<
      PensumResponse,
      { profileId: number; page: number; perPage: number; search: string }
    >({
      queryFn: async ({ profileId, page, search, perPage }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<PensumResponse>('/pensums', {
            params: {
              user_profile_id: profileId,
              page,
              size: perPage,
              type_sort: 'desc',
              search,
            },
          });

          dispatch(setPensumList(data.data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Pensums', id } as const)),
              { type: 'Pensums', id: 'LIST' },
            ]
          : [{ type: 'Pensums', id: 'LIST' }],
    }),

    /**
     * @GET all pensums avalibles
     */
    getAvaliblesPensums: builder.query<Pensum[], { profileId: number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get<Pensum[]>('/pensums', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'PensumsAvailables', id: 'COMBO_PENSUMS_LIST' }],
    }),

    /**
     * @GET period by id
     */
    getPensum: builder.query<Pensum, { profileId: number; pensumId: number }>({
      queryFn: async ({ profileId, pensumId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<Pensum>('/pensums', {
            params: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'Pensums', profileId }],
    }),

    /**
     * @POST create pensum
     */
    addPensum: builder.mutation<Pensum, { profileId: number; pensumPayload: Partial<Pensum> }>({
      queryFn: async ({ profileId, pensumPayload }) => {
        try {
          const { data } = await mainApi.post<Pensum>('/pensums', {
            user_profile_id: profileId,
            ...pensumPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Pensums', id: 'LIST' }],
    }),

    /**
     * @PUT update pensum
     */
    updatePensum: builder.mutation<
      Pensum,
      { profileId: number; pensumId: number; pensumPayload: Partial<Pensum> }
    >({
      queryFn: async ({ profileId, pensumId, pensumPayload }) => {
        try {
          const { data } = await mainApi.put<Pensum>(`/pensums/${pensumId}`, {
            user_profile_id: profileId,
            ...pensumPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Pensums', id: 'LIST' }],
    }),

    /**
     * @DELETE remove pensum
     */
    deletePensum: builder.mutation<Pensum, { profileId: number; pensumId: number }>({
      queryFn: async ({ profileId, pensumId }) => {
        try {
          const { data } = await mainApi.delete(`pensums/${pensumId}`, {
            data: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Pensums', id: 'LIST' }],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetPensumsQuery,
  useGetAvaliblesPensumsQuery,
  useAddPensumMutation,
  useDeletePensumMutation,
  useGetPensumQuery,
  useUpdatePensumMutation,
} = pensumApi;
